import React, { useState } from 'react';
import { Modal, Button } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { getTheme } from 'helpers/getTheme';
import { useAppSelector } from 'store/hook';
import billing from 'api/billing';
import classes from './CancelSubscriptionModal.module.scss';

interface CancelSubscriptionModalProps {
  isOpen: boolean;
  subscriptionId: string | null;
  tariffName?: string;
  nextBillingAt?: string | null;
  onClose: () => void;
  onSuccessCancel: () => void;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  isOpen,
  subscriptionId,
  tariffName = '',
  nextBillingAt,
  onClose,
  onSuccessCancel,
}) => {
  const { t } = useTranslation();
  const { messageApi } = useAppSelector((state) => state.windowState);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleConfirmCancel = async () => {
    if (!subscriptionId) return;

    setIsLoading(true);
    try {
      await billing.cancelSubscription(subscriptionId);
      messageApi?.success(t('Billing.cancelSubscriptionModal.successNotice'));
      onSuccessCancel();
      onClose();
    } catch {
      messageApi?.error(t('Billing.cancelSubscriptionModal.errorNotice'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      width={480}
      title={t('Billing.cancelSubscriptionModal.title')}
      footer={null}
      destroyOnClose
      centered
      className={cn(classes.modal, getTheme())}
    >
      <div className={classes.content}>
        <div className={classes.warningBanner}>
          <WarningOutlined className={classes.warningIcon} />
          <div className={classes.warningText}>
            {nextBillingAt
              ? t('Billing.cancelSubscriptionModal.warningNotice', {
                  tariff: tariffName,
                  date: nextBillingAt,
                })
              : t('Billing.cancelSubscriptionModal.warningNoticeNoDate', {
                  tariff: tariffName,
                })}
          </div>
        </div>

        <div className={classes.footer}>
          <Button onClick={onClose} className={classes.cancelButton}>
            {t('Billing.cancelSubscriptionModal.cancel')}
          </Button>
          <Button
            danger
            type="primary"
            loading={isLoading}
            onClick={handleConfirmCancel}
            className={classes.confirmCancelButton}
          >
            {t('Billing.cancelSubscriptionModal.confirmCancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CancelSubscriptionModal;
